import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Stack, TextField, Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Helpers from "../../Config/Helpers";

const errorText = (error) => error?.response?.data?.error || "Unable to complete this request. Please try again.";
const newRequestId = () => window.crypto?.randomUUID?.() || `email-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const statusLabels = { sent: "Sent", sending: "Sending / unconfirmed", failed: "Failed", unknown: "Unconfirmed" };

export default function ReceiptEmailDialog({ receipt }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [source, setSource] = useState(null);
  const [uncertain, setUncertain] = useState(false);
  const sendAttempt = useRef(null);
  const actionLock = useRef(false);
  const receiptId = receipt?._id;
  const isEstimate = String(receipt?.receiptType || "Quote").toLowerCase() === "quote";
  const documentType = isEstimate ? "Estimate" : "Receipt";
  const number = receipt?.friendlyNumber || receipt?.invoiceNumber || String(receiptId || "").slice(-6).toUpperCase();
  const base = `${Helpers.baseUrl}/receipt-emails`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [config, records] = await Promise.all([
        axios.get(`${base}/settings`, Helpers.headers),
        axios.get(`${base}/${receiptId}/history`, Helpers.headers),
      ]);
      setSettings(config.data);
      setHistory(records.data);
    } catch (requestError) { setError(errorText(requestError)); }
    finally { setLoading(false); }
  }, [base, receiptId]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    setOpen(false);
    sendAttempt.current = null;
    setUncertain(false);
  }, [receiptId]);

  const composeCurrent = () => {
    setSource(null);
    setRecipient(receipt?.customer?.email || "");
    setSubject(`Your ${documentType.toLowerCase()} #${number} from ${Helpers.authShop?.name || "our shop"}`);
    setMessage(`Hello${receipt?.customer?.name ? ` ${receipt.customer.name}` : ""},\n\nPlease find your ${documentType.toLowerCase()} attached. Reply to this email if you have any questions.\n\nThank you for your business!`);
    setError("");
    setNotice("");
  };
  const openDialog = () => {
    if (!sendAttempt.current) composeCurrent();
    setOpen(true);
  };
  const resend = (record) => {
    setSource(record);
    setRecipient(record.recipient);
    setSubject(record.subject);
    setMessage(record.message);
    setError("");
    setNotice("");
  };
  const verification = async (confirm) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${base}/verify/${confirm ? "confirm" : "request"}`, confirm ? { code } : {}, Helpers.headers);
      if (confirm) {
        setNotice("Shop email verified. You can now send estimates and receipts.");
        setCodeSent(false);
        setCode("");
        await load();
      } else {
        setCodeSent(true);
        setNotice("A verification code was sent to your shop email. It expires in 10 minutes.");
      }
    } catch (requestError) { setError(errorText(requestError)); }
    finally { setBusy(false); actionLock.current = false; }
  };
  const download = async (record) => {
    setError("");
    try {
      const url = record ? `${base}/${receiptId}/history/${record._id}/pdf` : `${base}/${receiptId}/pdf`;
      const response = await axios.get(url, { ...Helpers.headers, responseType: "blob",
        ...(!record ? { params: { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone } } : {}),
      });
      const objectUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = record?.filename || `${documentType}-${number}.pdf`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (_) { setError("Could not download this PDF. Please try again."); }
  };
  const send = async (event) => {
    event.preventDefault();
    if (actionLock.current) return;
    actionLock.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    // Metadata only: the server builds the current PDF from receiptId or loads
    // sourceEmailId's exact archived bytes. Never serialize a PDF Blob here.
    if (!sendAttempt.current) sendAttempt.current = {
      recipient: recipient.trim(), subject: subject.trim(), message, requestId: newRequestId(),
      ...(source ? { sourceEmailId: source._id } : {
        documentUpdatedAt: receipt.updatedAt,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    };
    try {
      const response = await axios.post(`${base}/${receiptId}/send`, sendAttempt.current, Helpers.headers);
      const record = response.data;
      if (record.status === "sending") {
        setUncertain(true);
        setNotice("This email is still sending or its result is unconfirmed. Check its status before starting another send.");
      } else {
        sendAttempt.current = null;
        setUncertain(false);
        if (record.status === "sent") setNotice(`Email sent to ${record.recipient}.`);
        else setError(record.error || "Sending could not be confirmed.");
      }
      await load();
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status >= 400 && status < 500) {
        sendAttempt.current = null;
        setUncertain(false);
        setError(errorText(requestError));
      } else {
        setUncertain(true);
        setError(`${errorText(requestError)} Use “Check send status” before sending again; this reuses the same request to prevent duplicates.`);
      }
    } finally { setBusy(false); actionLock.current = false; }
  };
  const canSend = settings?.configured && settings?.verified;

  return <>
    <Button variant="contained" startIcon={<EmailOutlinedIcon />} disabled={!receiptId} onClick={openDialog}
      className="d-print-none" sx={{
        fontSize: 12,
        fontWeight: 400,
        textTransform: "none",
        whiteSpace: "nowrap",
        backgroundColor: "#36589A",
        borderRadius: 4,
        padding: "4px 8px",
        fontFamily: 'Inter, "Segoe UI", sans-serif',
        "&:hover": {
          backgroundColor: "#2E4D88",
          color: "#FFFFFF !important",
        },
      }}>
      Email
    </Button>
    <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} fullWidth maxWidth="md">
      <DialogTitle>Email {documentType} #{number}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity={uncertain ? "warning" : "success"}>{notice}</Alert>}
          {loading && <Typography>Loading email details…</Typography>}
          {settings && !settings.configured && <Alert severity="info">
            Email sending needs to be enabled by your platform administrator. You can still download the PDF below.
          </Alert>}
          {settings?.configured && !settings.verified && <Box>
            <Alert severity="info" sx={{ mb: 1 }}>
              Verify your shop’s email{settings.replyTo ? ` (${settings.replyTo})` : ""} so customer replies go to the right inbox.
              {!settings.replyTo && " Save an email address in Shop Profile first."}
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button onClick={() => verification(false)} disabled={busy || !settings.replyTo}>Send verification code</Button>
              {codeSent && <>
                <TextField size="small" label="Verification code" value={code} onChange={(event) => setCode(event.target.value)}
                  inputProps={{ maxLength: 6, inputMode: "numeric" }} disabled={busy} />
                <Button onClick={() => verification(true)} disabled={busy || !/^\d{6}$/.test(code)}>Verify</Button>
              </>}
            </Stack>
          </Box>}
          {settings?.configured && <Typography variant="body2" color="text.secondary">
            From: {settings.fromName} &lt;{settings.fromAddress}&gt;<br />Replies go to: {settings.replyTo || "Set your shop email in Shop Profile"}
          </Typography>}
          {source && <Alert severity="info" action={<Button disabled={busy || uncertain} onClick={composeCurrent}>Use current document</Button>}>
            Resending the saved PDF from {new Date(source.createdAt).toLocaleString()}. Later edits to the receipt are not included.
          </Alert>}
          <Box component="form" id="receipt-email-form" onSubmit={send}>
            <Stack spacing={2}>
              <TextField label="To" type="email" required value={recipient} onChange={(event) => setRecipient(event.target.value)}
                disabled={busy || uncertain} inputProps={{ maxLength: 254 }} fullWidth />
              <TextField label="Subject" required value={subject} onChange={(event) => setSubject(event.target.value)}
                disabled={busy || uncertain} inputProps={{ maxLength: 200 }} fullWidth />
              <TextField label="Message" value={message} onChange={(event) => setMessage(event.target.value)}
                disabled={busy || uncertain} inputProps={{ maxLength: 5000 }} multiline minRows={4} fullWidth />
              <Button startIcon={<DownloadOutlinedIcon />} onClick={() => download(source)} sx={{ alignSelf: "flex-start" }}>
                Download {source ? "saved" : "current"} PDF
              </Button>
            </Stack>
          </Box>
          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Email history</Typography>
            <Button onClick={load} disabled={loading || busy}>Refresh</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Latest 100 emails for this document. “Sent” means the email service accepted the message; it does not confirm inbox delivery.
          </Typography>
          {!loading && !history.length && <Typography color="text.secondary">No emails sent yet.</Typography>}
          {history.map((record) => <Box key={record._id} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <Chip size="small" label={statusLabels[record.status] || record.status}
                color={record.status === "sent" ? "success" : record.status === "failed" ? "error" : "warning"} />
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{record.recipient}</Typography>
              <Typography variant="caption" color="text.secondary">{new Date(record.sentAt || record.createdAt).toLocaleString()}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>{record.subject}</Typography>
            {record.error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{record.error}</Typography>}
            {record.status === "sending" && <Typography variant="caption" color="text.secondary">
              If this status persists, ask your administrator to check delivery before resending.
            </Typography>}
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => download(record)}>Download PDF</Button>
              <Button size="small" onClick={() => resend(record)} disabled={busy || uncertain || record.status === "sending"}>Resend…</Button>
            </Stack>
          </Box>)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} disabled={busy}>Close</Button>
        <Button type="submit" form="receipt-email-form" variant="contained" disabled={busy || loading || (!uncertain && !canSend)}>
          {busy ? "Please wait…" : uncertain ? "Check send status" : "Send email"}
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}
